import {
  AiEnabledGuard,
  AiNotEnabledFallback,
  FeatureFlagGuard,
  PlanFeatureGuard,
  PlanGuard,
} from '@/components/guards';
import { Suspense, useEffect, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { PageLoader, useSnackbars } from '@customerio/pluma-components/react';
import { Outlet, useRouter } from '@tanstack/react-router';
import NotFoundPage from '@/pages/not-found.page';
import { BrandLoader } from '@/components/page/brand-loader';
import { GeosetRequiredGuard } from '@/pages/workspaces/geofences/geoset-required-guard';
import { NoAccess as OutcomesNoAccess } from '@/pages/workspaces/outcomes/no-access';
import { ExperimentsUnavailable } from '@/pages/workspaces/experimentation-hub/components/experiments-unavailable';
import { ByokFlagGate } from '@/pages/workspaces/settings/llm-configuration/components/byok-flag-gate';
import { useListWorkspaces } from '@/features/workspaces';
import { isSessionRejection, useAuth, useAuthQuery } from '@/services/auth';
import { accountStore } from '@/services/auth/store';
import { PROMO_CHECKOUT_PATH, requiresCardCheckout } from '@/services/auth/promo-checkout';
import { AppErrorFallback } from '@/components/app-error-boundary/app-error-fallback';
import { Navigate } from '@/utils/navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { generatePath } from '@/services/router/generate-path';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { billing } from '@/services/billing';
import { VERIFICATION_REJECTED_MESSAGE } from '@/services/billing/messages';
import { SafeNavigate, useSafeNavigate } from '@/services/router/safe-navigate';
import { BILLING_PATHS } from '@/features/billing';
import { ONBOARDING_PATHS, useOnboardingRedirect } from '@/features/onboarding';
import Journeys from '@/pages/journeys';
import { AccountState, PlanFeature } from '@/services/auth/types';
import { BUILDER_PLAN_SLUG, isBuilderPlan } from '@/services/auth/plan-slugs';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { RedirectIfMasqReadOnly } from '@/services/router/components/redirect-if-masq-read-only';
import type { Permission } from '@customerio/hydra-permissions';
import {
  AIBusinessProfile,
  AccountApiCredentialsPage,
  AccountApiKeysPage,
  AccountPartnerCreatePage,
  AccountPartnerPage,
  ServiceAccountDetailPage,
  AccountSettingsEditPage,
  AccountSettingsPage,
  AiSettingsPage,
  SettingsHomePage,
  SlackSettingsPage,
  SlackLinkPage,
  AssetsPage,
  AuditLogsPage,
  DeliverabilityPage,
  DesignStudioDashboardPage,
  DesignStudioGlobalStylesPage,
  EmailClientsPage,
  ExperimentalFeaturesPage,
  ExportsPage,
  GoogleIdentitiesPage,
  LayoutsPage,
  HarSanitizerPage,
  LayoutDetailPage,
  LayoutEditPage,
  LayoutStartersPage,
  MessageLibraryPage,
  NewLayoutPage,
  NewRolePage,
  NoAccessibleWorkspacesPage,
  AcceptGuestInvitationPage,
  PersonalSecurityBackupCodesPage,
  PersonalSecurityNewBackupCodesPage,
  PersonalSecurityNewPage,
  PersonalSecurityNewTotpPage,
  PersonalSecurityPage,
  PersonalSettingsPage,
  PlansAndBillingPage,
  YourPlanPage,
  BillingOverviewPage,
  BillingUsagePage,
  LlmUsagePage,
  InboxPreviewUsagePage,
  ProfileGrowthPage,
  BillingHistoryPage,
  AvailablePlansPage,
  PaymentMethodsPage,
  BillingDetailsPage,
  TrialExpiredPage,
  CheckoutPage,
  PromoCheckoutPage,
  PostCancelPage,
  AccountCanceledPage,
  PrivacySettingsPage,
  RolePage,
  RolesPage,
  SecuritySettingsPage,
  SsoSettingsPage,
  TeamMembersPage,
  TeamMemberAddPage,
  TeamMemberEditPage,
  ApproveJoinRequestPage,
  TeamMemberInviteGuestPage,
  SnippetsLayout,
  ExperimentsLayout,
  WorkspaceApiCredentialsPage,
  WorkspaceApiKeysPage,
  OutcomesListPage,
  WorkspaceAutoArchivePage,
  WorkspaceBoardsPage,
  WorkspaceBoardPage,
  WorkspaceConfirmDeletePage,
  WorkspaceEditPage,
  WorkspaceGeofencesSettingsPage,
  WorkspaceAuditLogsPage,
  WorkspaceLanguageSettingsPage,
  WorkspaceLlmConfigurationIndexPage,
  WorkspaceLlmConfigurationNewPage,
  WorkspaceLlmConfigurationEditPage,
  WorkspaceObjectsSettingsPage,
  WorkspaceObjectsSettingsEditPage,
  WorkspaceObjectsSettingsNewPage,
  WorkspaceMergeOptionsPage,
  WorkspaceNewPage,
  WorkspaceSuppressionsPage,
  WorkspaceConsentPage,
  WorkspacesSettingsPage,
  WorkspaceCustomSmsSettingsPage,
  WorkspaceCustomSmsCredentialDetailPage,
  WorkspaceUrlParamsSettingsPage,
  WorkspaceUrlParamsEditPage,
  WorkspaceTimeZoneMatchSettingsPage,
  WorkspaceSlackSettingsPage,
  WorkspaceLineSettingsPage,
  WorkspaceUrbanAirshipSettingsPage,
  WorkspaceMessageFrequencyLimitsPage,
  WorkspaceEmailSettingsPage,
  WorkspaceEmailSendingDomainsPage,
  WorkspaceEmailSendingDomainDetailPage,
  WorkspaceEmailDynamicAddressesPage,
  WorkspaceEmailSmtpPage,
  WorkspaceEmailDefaultsPage,
  WorkspaceEmailSuppressionListPage,
  WebhookConfigurationPage,
  WebhookConfigurationNewPage,
  WebhookConfigurationEditPage,
  OnboardingVerificationPage,
  OnboardingWizardPage,
  WorkflowBuilderPage,
} from './-route-pages';
import { LastWorkspaceResolver, LazyPage } from './-route-layouts';
import { AppBundle } from '@/utils/app-bundles';
import { AnalysisRouteView, type AnalysisSegment } from '@/pages/workspaces/analysis/routing';
import {
  OverviewWorkflowRouteView,
  WorkflowBuilderRouteView,
} from '@/pages/workspaces/automations/[campaignId]/workflow/routing';
import { type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';

export function AccountApiKeysRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <AccountApiKeysPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountApiCredentialsRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <AccountApiCredentialsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceApiCredentialsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceApiCredentialsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function AccountServiceAccountRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <ServiceAccountDetailPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceServiceAccountRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <ServiceAccountDetailPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function AccountAuditLogsRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AuditLogsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function ExperimentalFeaturesRoute() {
  return (
    <LazyPage>
      <ExperimentalFeaturesPage />
    </LazyPage>
  );
}

// The account settings landing page. Ember admitted every authenticated user; the cards self-filter
// by permission, so there is no ProtectedRoute gate here.
export function SettingsHomeRoute() {
  return (
    <LazyPage>
      <SettingsHomePage />
    </LazyPage>
  );
}

// Security settings index (FPS-2223). Hydra owns `/settings/security` unconditionally
// after the FPS-2767 cutover — no rollout flag, no embedded fallback. The nested
// `/settings/security/sso` page (FPS-2737) is likewise Hydra-owned — see
// `SsoSettingsRoute` below.
export function SecuritySettingsRoute() {
  // Ember's parent `settings/security` route gated on `account/view` and rendered the
  // panels read-only (not hidden) for users without `account/edit`, so a read-only
  // masquerader is admitted to view.
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <SecuritySettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// Security → SSO page (FPS-2737). Hydra owns `/settings/security/sso` unconditionally
// after the FPS-2767 cutover, on the same footing as the security index above — no
// rollout flag, and the Ember pod is deleted.
export function SsoSettingsRoute() {
  // Mirrors Ember's parent `settings/security` gate (`account/view`, panels rendered
  // read-only without `account/edit`), so a read-only masquerader is admitted to view.
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <SsoSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// Plans & Billing shell. Layout route for `/settings/billing` and its tabs.
export function BillingRoute() {
  // Every billing tab requires `account/billing/view`, so the gate lives here on the shell — no
  // child tab is reachable without it. Read-only masqueraders who hold the permission are
  // admitted, which is ProtectedRoute's default.
  return (
    <ProtectedRoute permission="account/billing/view" redirectTo="/settings">
      <LazyPage>
        <PlansAndBillingPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// The top-level, chrome-less promo checkout page. It renders outside the `_authenticated` layout and
// reads the authenticated account itself — including its own auth-loading state — and redirects when
// the account no longer needs checkout, so it needs no `ProtectedRoute` chrome.
export function PromoCheckoutRoute() {
  return (
    <LazyPage>
      <PromoCheckoutPage />
    </LazyPage>
  );
}

// `/settings/receipts` redirects into Billing history. The route is nested under `_authenticated`,
// which already holds render until auth settles, so the redirect target is known on the first paint.
export function ReceiptsRedirectRoute() {
  return <Navigate to={BILLING_PATHS.billingHistory} replace={true} />;
}

// `/settings/subscription` redirects into the Your Plan tab (FPS-2286), on the same footing as the
// receipts redirect above — nested under `_authenticated`, so the target is known on first paint.
export function SubscriptionRedirectRoute() {
  return <Navigate to={BILLING_PATHS.yourPlan} replace={true} />;
}

// Your Plan tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function YourPlanRoute() {
  const { account } = useAuth();
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const subscription = useStore(billing.subscription);
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const navigate = useSafeNavigate();

  // Ordway escape-hatch: an Ordway account that isn't in the `current` state and has no subscription
  // data has nothing to show here, so send it to available plans. Skip this when verification is
  // rejected — Available Plans redirects those accounts straight back to Your Plan, so redirecting
  // there would bounce the user between the two tabs forever.
  const shouldRedirect =
    isOrdwayCustomer &&
    account?.account_state !== AccountState.Current &&
    subscription == null &&
    !isVerificationRejected;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(BILLING_PATHS.availablePlans);
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <LazyPage>
      <YourPlanPage />
    </LazyPage>
  );
}

type BillingUI2026RouteProps = {
  children: ReactNode;
  /** Where a flag-off account lands. Defaults to Your plan, which exists either way. */
  redirectTo?: string;
};

// The tabs the `billing-ui-2026` flag introduces. Each renders inside the billing shell layout,
// which already gates on `account/billing/view`; with the flag off, their URLs redirect to the tab
// they replace.
function BillingUI2026Route({ children, redirectTo = BILLING_PATHS.yourPlan }: BillingUI2026RouteProps) {
  const isBillingUI2026Enabled = useFeatureFlag(AccountFeatureFlag.BillingUI2026);
  const navigate = useSafeNavigate();

  useEffect(() => {
    if (!isBillingUI2026Enabled) {
      navigate(redirectTo);
    }
  }, [isBillingUI2026Enabled, navigate, redirectTo]);

  if (!isBillingUI2026Enabled) {
    return null;
  }

  return <LazyPage>{children}</LazyPage>;
}

// Overview tab: what the account will be charged, and why.
export function BillingOverviewRoute() {
  return (
    <BillingUI2026Route>
      <BillingOverviewPage />
    </BillingUI2026Route>
  );
}

// Usage tab.
export function BillingUsageRoute() {
  return (
    <BillingUI2026Route>
      <BillingUsagePage />
    </BillingUI2026Route>
  );
}

// Billing details tab: everything that documents or determines a charge — card, billing contact,
// plan allotments, and (PAYG) auto-refill. It redirects to Payment methods rather than Your plan,
// because Payment methods is the tab it replaces.
export function BillingDetailsRoute() {
  return (
    <BillingUI2026Route redirectTo={BILLING_PATHS.paymentMethods}>
      <BillingDetailsPage />
    </BillingUI2026Route>
  );
}

// Credit usage tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function LlmUsageRoute() {
  return (
    <LazyPage>
      <LlmUsagePage />
    </LazyPage>
  );
}

// Inbox Preview Usage tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function InboxPreviewUsageRoute() {
  return (
    <LazyPage>
      <InboxPreviewUsagePage />
    </LazyPage>
  );
}

// Profile growth tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`. The tab and page are additionally gated on the `profile_growth` account
// flag: when it's off, this URL redirects to the Your Plan tab (matching the Ember route's
// `beforeModel` guard).
export function ProfileGrowthRoute() {
  const isProfileGrowthEnabled = useFeatureFlag(AccountFeatureFlag.ProfileGrowth);
  const navigate = useSafeNavigate();

  useEffect(() => {
    if (!isProfileGrowthEnabled) {
      navigate(BILLING_PATHS.yourPlan);
    }
  }, [isProfileGrowthEnabled, navigate]);

  if (!isProfileGrowthEnabled) {
    return null;
  }

  return (
    <LazyPage>
      <ProfileGrowthPage />
    </LazyPage>
  );
}

// Billing history tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function BillingHistoryRoute() {
  return (
    <LazyPage>
      <BillingHistoryPage />
    </LazyPage>
  );
}

// Available plans (Journeys) tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`. An account whose verification has been rejected can't pick a plan here, so
// it's redirected to the Your Plan tab and shown the verification-rejected alert.
export function AvailablePlansRoute() {
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const navigate = useSafeNavigate();
  const snackbars = useSnackbars();

  useEffect(() => {
    if (isVerificationRejected) {
      snackbars.create({
        variant: 'critical',
        id: 'billing.verification-rejected',
        message: VERIFICATION_REJECTED_MESSAGE,
      });
      navigate(BILLING_PATHS.yourPlan);
    }
  }, [isVerificationRejected, navigate, snackbars]);

  if (isVerificationRejected) {
    return null;
  }

  return (
    <LazyPage>
      <AvailablePlansPage />
    </LazyPage>
  );
}

// Payment Methods tab. Renders inside the billing shell layout (already gates on
// `account/billing/view`), so this adds no ProtectedRoute of its own.
export function PaymentMethodsRoute() {
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const isTrial = useStore(billing.isTrial);
  const isPendingCheckout = useStore(billing.isPendingCheckout);
  const isBuilderPlan = useStore(billing.isBuilderPlan);
  const navigate = useSafeNavigate();

  // An Ordway trial that hasn't started checkout and isn't on the Builder plan has no payment method
  // to manage yet, so send it to available plans (that page isn't migrated yet, so navigate by string
  // path).
  const shouldRedirect = isOrdwayCustomer && isTrial && !isPendingCheckout && !isBuilderPlan;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(BILLING_PATHS.availablePlans);
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <LazyPage>
      <PaymentMethodsPage />
    </LazyPage>
  );
}

// Checkout tab. Renders inside the billing shell layout (already gates on `account/billing/view`).
// Mirrors the Ember route's beforeModel redirect: a verification-rejected account can't check out and
// goes to Your Plan with an alert.
export function CheckoutRoute() {
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const navigate = useSafeNavigate();
  const snackbars = useSnackbars();

  const redirectMessage = isVerificationRejected ? VERIFICATION_REJECTED_MESSAGE : null;

  useEffect(() => {
    if (redirectMessage != null) {
      snackbars.create({ variant: 'critical', id: 'billing.checkout.redirect', message: redirectMessage });
      navigate(BILLING_PATHS.yourPlan);
    }
  }, [redirectMessage, navigate, snackbars]);

  if (redirectMessage != null) {
    return null;
  }

  return (
    <LazyPage>
      <CheckoutPage />
    </LazyPage>
  );
}

// Hydra owns `/trial-expired/journeys` unconditionally. There is no rollout flag and no embedded
// fallback.
export function TrialExpiredRoute() {
  const { account } = useAuth();

  // An account with messaging access has no paywall to see, so it returns to the Journeys dashboard.
  // This URL carries no workspace, so `last` resolves one from the session. The embedded app serves the
  // dashboard, which is why this uses SafeNavigate.
  if (account.planFeatures.messaging.expired !== true) {
    return (
      <SafeNavigate
        to={generatePath('/workspaces/:workspaceId/journeys/dashboard', { workspaceId: 'last' })}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  return (
    <LazyPage>
      <TrialExpiredPage />
    </LazyPage>
  );
}

export function GoogleIdentitiesRoute() {
  return (
    <LazyPage>
      <GoogleIdentitiesPage />
    </LazyPage>
  );
}

// Personal settings index (FPS-2861). Flag off: the embedded Ember app serves the URL. Route
// ownership (`route-ownership.ts`) falls back to `embedded` on the same flag, so the ownership
// answer and the rendered app agree. No ProtectedRoute — every signed-in user (viewers included)
// manages their own profile and password here.
export function PersonalSettingsRoute() {
  const isMigrationEnabled = useFeatureFlag(AccountFeatureFlag.PersonalSettingsInHydra);

  if (!isMigrationEnabled) {
    return <Journeys />;
  }

  return (
    <LazyPage>
      <PersonalSettingsPage />
    </LazyPage>
  );
}

type PersonalSecurityMigrationProps = { children: ReactNode };

/**
 * The personal 2FA screens (FPS-2292), on the same `personal_settings_in_hydra`
 * flag as the settings index: /settings/personal migrates as one project, so a
 * single toggle moves a user's whole personal-settings experience. Flag off, the
 * embedded Ember app serves the URL — which is also what `route-ownership.ts`
 * answers on that flag, so the ownership answer and the app on screen never
 * disagree.
 *
 * No ProtectedRoute: every signed-in user manages their own 2FA. Masquerade is
 * read-only rather than redirected — the status and TOTP screens render
 * read-only, the three backup-code screens refuse, since those mint or reveal
 * credentials.
 */
function PersonalSecurityMigration({ children }: PersonalSecurityMigrationProps) {
  const isMigrationEnabled = useFeatureFlag(AccountFeatureFlag.PersonalSettingsInHydra);

  if (!isMigrationEnabled) {
    return <Journeys />;
  }

  return <LazyPage>{children}</LazyPage>;
}

export function PersonalSecurityRoute() {
  return (
    <PersonalSecurityMigration>
      <PersonalSecurityPage />
    </PersonalSecurityMigration>
  );
}

export function PersonalSecurityNewRoute() {
  return (
    <PersonalSecurityMigration>
      <PersonalSecurityNewPage />
    </PersonalSecurityMigration>
  );
}

export function PersonalSecurityNewTotpRoute() {
  return (
    <PersonalSecurityMigration>
      <PersonalSecurityNewTotpPage />
    </PersonalSecurityMigration>
  );
}

export function PersonalSecurityBackupCodesRoute() {
  return (
    <PersonalSecurityMigration>
      <PersonalSecurityBackupCodesPage />
    </PersonalSecurityMigration>
  );
}

export function PersonalSecurityNewBackupCodesRoute() {
  return (
    <PersonalSecurityMigration>
      <PersonalSecurityNewBackupCodesPage />
    </PersonalSecurityMigration>
  );
}

type RolesGuardProps = { children: ReactNode };

function RolesGuard({ children }: RolesGuardProps) {
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <PlanFeatureGuard feature={PlanFeature.CustomRoles} redirectTo="/settings">
        {children}
      </PlanFeatureGuard>
    </ProtectedRoute>
  );
}

export function RolesRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RolesGuard>
  );
}

export function TeamMembersRoute() {
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMembersPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberAddRoute() {
  // Ember admitted any team-members viewer and rendered the form read-only for those without
  // edit permission; the page gates the fields + Send button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberAddPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberEditRoute() {
  // Ember admitted any team-members viewer and rendered the form read-only for those without
  // edit permission; the page gates the fields + Save button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberEditPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function ApproveJoinRequestRoute() {
  // Same access model as add/edit: any team-members viewer is admitted; the page gates the fields +
  // Approve button on account/team_members/edit. No flag guard — a join request only exists if the
  // pre-signup mint flag was on for the requester, so the page is inherently dark otherwise (a
  // direct URL visit just shows the "no longer pending" empty state).
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <ApproveJoinRequestPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberInviteGuestRoute() {
  // Same access model as add/edit: any team-members viewer is admitted; the page gates the fields +
  // Send button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberInviteGuestPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspacesSettingsRoute() {
  return (
    <ProtectedRoute
      permission="account/workspaces/create"
      redirectTo="/settings"
      allowReadOnlyMasqAccess={true}
      errorMessage="You do not have permission to view this page. If you'd like access, let an Admin know."
    >
      <LazyPage>
        <WorkspacesSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceNewRoute() {
  // Ember admitted any account/view user and rendered the form read-only for those without
  // create permission; the page gates the form on account/workspaces/create.
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <WorkspaceNewPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function NewRoleRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <NewRolePage />
      </LazyPage>
    </RolesGuard>
  );
}

export function RoleRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <RolePage />
      </LazyPage>
    </RolesGuard>
  );
}

// The AI, Slack Agent and Privacy settings cards are gated on account/support_access/view, so
// their routes admit that permission. Each page also holds sections whose own gates are
// independent of it, so the routes admit those permissions too — otherwise a role with, say,
// account/edit but no support access loses the only route to the controls it may use.
// Masqueraders keep access, as the card list admits them too.
export function AccountAiRoute() {
  return (
    <ProtectedRoute
      anyPermissions={['account/support_access/view', 'account/edit']}
      redirectTo="/settings"
      allowReadOnlyMasqAccess={true}
    >
      <LazyPage>
        <AiSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountSlackRoute() {
  return (
    <ProtectedRoute
      anyPermissions={['account/support_access/view', 'account/edit']}
      redirectTo="/settings"
      allowReadOnlyMasqAccess={true}
    >
      <LazyPage>
        <SlackSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountSlackLinkRoute() {
  return (
    <LazyPage>
      <SlackLinkPage />
    </LazyPage>
  );
}

export function AccountPrivacyRoute() {
  // Cookie settings and external avatars sit on this page behind account/cookie_settings/* and
  // account/edit, so both permissions admit the route as well.
  return (
    <ProtectedRoute
      anyPermissions={['account/support_access/view', 'account/cookie_settings/view', 'account/edit']}
      redirectTo="/settings"
      allowReadOnlyMasqAccess={true}
    >
      <LazyPage>
        <PrivacySettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountSettingsRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AccountSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountSettingsEditRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AccountSettingsEditPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// Layout for /settings/partner and its children (the list index and the create
// page). Gating lives here so it applies to every nested route, and the matched
// child renders through the Outlet.
export function AccountPartnerRoute() {
  // Gated on the account-level partner_account attribute (set in Flightdeck).
  const { account } = useAuth();
  if (account?.partner_account !== true) {
    return <NotFoundPage />;
  }
  return (
    <ProtectedRoute permission="account/edit" redirectTo="/settings">
      <Outlet />
    </ProtectedRoute>
  );
}

export function AccountPartnerIndexRoute() {
  return (
    <LazyPage>
      <AccountPartnerPage />
    </LazyPage>
  );
}

export function AccountPartnerCreateRoute() {
  return (
    <LazyPage>
      <AccountPartnerCreatePage />
    </LazyPage>
  );
}

export function WorkspaceAiBusinessProfileRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AIBusinessProfile />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceAuditLogsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WorkspaceAuditLogsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

// Webhook configuration index (FPS-2354).
export function WebhookConfigurationRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/configuration_manager/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
        errorMessage="You do not have permission to view webhook configuration. If you'd like access, let an Admin know."
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WebhookConfigurationPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

// Webhook configuration create (FPS-2354). `/new` is gated on
// `journeys/configuration_manager/create` and redirects a denied user to the
// index. Write routes don't admit read-only masqueraders, so no
// `allowReadOnlyMasqAccess` here (unlike the view-only index).
export function WebhookConfigurationNewRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/configuration_manager/create"
        redirectTo={(params) =>
          router.buildLocation({
            to: '/workspaces/$workspaceId/settings/webhook_configuration',
            params: { workspaceId: params.workspaceId },
          }).href
        }
        errorMessage="You do not have permission to create webhooks. If you'd like access, let an Admin know."
      >
        <LazyPage>
          <WebhookConfigurationNewPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

// Webhook configuration edit (FPS-2354). `/:id/edit` is gated on
// `journeys/configuration_manager/edit` and redirects a denied user to the
// index.
export function WebhookConfigurationEditRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/configuration_manager/edit"
        redirectTo={(params) =>
          router.buildLocation({
            to: '/workspaces/$workspaceId/settings/webhook_configuration',
            params: { workspaceId: params.workspaceId },
          }).href
        }
        errorMessage="You do not have permission to edit webhooks. If you'd like access, let an Admin know."
      >
        <LazyPage>
          <WebhookConfigurationEditPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceApiKeysRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceApiKeysPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

type OutcomesPermissionGateProps = { children: ReactNode };

// Suggestions act on live campaigns, so the page needs campaign-edit; the check sits inside LastWorkspaceResolver because `journeys/*` permissions are per-workspace.
function OutcomesPermissionGate({ children }: OutcomesPermissionGateProps) {
  const { hasPermission } = usePermissions();

  if (!hasPermission('journeys/campaigns/edit')) {
    return <OutcomesNoAccess />;
  }

  return <>{children}</>;
}

// Flag gate, AI opt-in, workspace resolver, and permission gate live on the layout, so both the list and a detail path pass through them.
export function OutcomesLayoutRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.OutcomeOptimization} fallback={OutcomesNoAccess}>
      <AiEnabledGuard fallback={AiNotEnabledFallback}>
        <LastWorkspaceResolver>
          <OutcomesPermissionGate>
            <Outlet />
          </OutcomesPermissionGate>
        </LastWorkspaceResolver>
      </AiEnabledGuard>
    </FeatureFlagGuard>
  );
}

export function OutcomesListRoute() {
  return (
    <LazyPage>
      <OutcomesListPage />
    </LazyPage>
  );
}

export function WorkspaceAutoArchiveRoute() {
  const router = useRouter();

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.SegmentAutoArchive} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceAutoArchivePage />
          </LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceBoardsRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DashboardEditorPoc} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <LazyPage>
          <WorkspaceBoardsPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceBoardRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DashboardEditorPoc} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <LazyPage>
          <WorkspaceBoardPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceEditRoute() {
  const router = useRouter();

  // Ember admitted workspace_settings/view users (and read-only masqueraders) and rendered the
  // form read-only for those without /edit; the page gates the form's readOnly on /edit. This
  // also matches the sibling settings routes (merge-options, auto-archive) that gate on /view.
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WorkspaceEditPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceConfirmDeleteRoute() {
  const router = useRouter();

  // The only entry point is the workspaces-table Delete link, which gates solely on
  // `account/workspaces/create`. Gate the route on that same permission (and nothing more) so
  // access exactly matches the link — requiring the per-workspace `workspace_settings/view` too
  // would redirect a create-capable user who followed a working link. No
  // `allowReadOnlyMasqAccess`: delete has no read-only mode, and read-only masqueraders lack the
  // create permission anyway.
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/workspaces/create"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceConfirmDeletePage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function ExportsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/exports/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <ExportsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function LayoutsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/layouts/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
          <LazyPage>
            <LayoutsPage />
          </LazyPage>
        </PlanGuard>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceMergeOptionsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceMergeOptionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceLanguageSettingsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/language_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceLanguageSettingsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceGeofencesSettingsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <PlanFeatureGuard
        feature={PlanFeature.Geofences}
        redirectTo={(params) =>
          router.buildLocation({
            to: '/workspaces/$workspaceId/settings',
            params: { workspaceId: params.workspaceId },
          }).href
        }
      >
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceGeofencesSettingsPage />
          </LazyPage>
        </ProtectedRoute>
      </PlanFeatureGuard>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceSuppressionsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceSuppressionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

// Consent lives behind the consent_records account flag; with the flag off the
// route hands back to the settings landing rather than rendering an empty page.
function WorkspaceConsentFlagOff() {
  const router = useRouter();
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  return (
    <Navigate
      to={
        router.buildLocation({
          to: '/workspaces/$workspaceId/settings',
          params: { workspaceId: workspaceId ?? 'last' },
        }).href
      }
      replace={true}
    />
  );
}

export function WorkspaceConsentRoute() {
  const router = useRouter();

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.ConsentRecords} fallback={WorkspaceConsentFlagOff}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceConsentPage />
          </LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceUrlParamsLayout() {
  const router = useRouter();
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        allowReadOnlyMasqAccess={true}
        redirectTo={(params) =>
          router.buildLocation({
            to: '/workspaces/$workspaceId/settings',
            params: { workspaceId: params.workspaceId ?? 'last' },
          }).href
        }
      >
        <Outlet />
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceUrlParamsSettingsRoute() {
  return (
    <LazyPage>
      <WorkspaceUrlParamsSettingsPage />
    </LazyPage>
  );
}

export function WorkspaceUrlParamsEditRoute() {
  const router = useRouter();
  return (
    <RedirectIfMasqReadOnly
      redirectTo={
        router.buildLocation({
          to: '/workspaces/$workspaceId/settings/actions/url_params',
          params: { workspaceId: getWorkspaceIdFromCurrentUrl() ?? 'last' },
        }).href
      }
    >
      <LazyPage>
        <WorkspaceUrlParamsEditPage />
      </LazyPage>
    </RedirectIfMasqReadOnly>
  );
}

type WorkspaceSettingsLeafRouteProps = {
  children: ReactNode;
};

/**
 * The access shape shared by the single-page workspace settings leaves under
 * `/settings/actions/*`: resolve the `last` workspace sentinel, admit viewers of
 * workspace settings (read-only masquerade included), and send everyone else to
 * the account settings landing — the workspace settings index applies the same
 * guard, so redirecting there would only bounce once more.
 */
function WorkspaceSettingsLeafRoute({ children }: WorkspaceSettingsLeafRouteProps) {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        allowReadOnlyMasqAccess={true}
        redirectTo="/settings"
        errorMessage="You do not have permission to view workspace settings. If you'd like access, let an admin know."
      >
        <LazyPage>{children}</LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceTimeZoneMatchRoute() {
  return (
    <WorkspaceSettingsLeafRoute>
      <WorkspaceTimeZoneMatchSettingsPage />
    </WorkspaceSettingsLeafRoute>
  );
}

export function WorkspaceSlackSettingsRoute() {
  return (
    <WorkspaceSettingsLeafRoute>
      <WorkspaceSlackSettingsPage />
    </WorkspaceSettingsLeafRoute>
  );
}

export function WorkspaceLineSettingsRoute() {
  return (
    <WorkspaceSettingsLeafRoute>
      <WorkspaceLineSettingsPage />
    </WorkspaceSettingsLeafRoute>
  );
}

export function WorkspaceUrbanAirshipSettingsRoute() {
  return (
    <WorkspaceSettingsLeafRoute>
      <WorkspaceUrbanAirshipSettingsPage />
    </WorkspaceSettingsLeafRoute>
  );
}

export function WorkspaceMessageFrequencyLimitsRoute() {
  return (
    <WorkspaceSettingsLeafRoute>
      <WorkspaceMessageFrequencyLimitsPage />
    </WorkspaceSettingsLeafRoute>
  );
}

export function WorkspaceCustomSmsLayout() {
  const router = useRouter();
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({
            to: '/workspaces/$workspaceId/settings',
            params: { workspaceId: params.workspaceId },
          }).href
        }
      >
        <Outlet />
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceLlmConfigurationIndexRoute() {
  const router = useRouter();
  return (
    <LastWorkspaceResolver>
      <ByokFlagGate>
        <ProtectedRoute
          permission="journeys/configuration_manager/view"
          errorMessage="You do not have permission to view LLM configuration. If you'd like access, let an Admin know."
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceLlmConfigurationIndexPage />
          </LazyPage>
        </ProtectedRoute>
      </ByokFlagGate>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceLlmConfigurationNewRoute() {
  const router = useRouter();
  return (
    <LastWorkspaceResolver>
      <ByokFlagGate>
        <ProtectedRoute
          permission="journeys/configuration_manager/create"
          errorMessage="You do not have permission to create LLM configurations. If you'd like access, let an Admin know."
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/llm_configuration',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceLlmConfigurationNewPage />
          </LazyPage>
        </ProtectedRoute>
      </ByokFlagGate>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceLlmConfigurationEditRoute() {
  const router = useRouter();
  return (
    <LastWorkspaceResolver>
      <ByokFlagGate>
        <ProtectedRoute
          permission="journeys/configuration_manager/edit"
          errorMessage="You do not have permission to edit LLM configurations. If you'd like access, let an Admin know."
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/llm_configuration',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceLlmConfigurationEditPage />
          </LazyPage>
        </ProtectedRoute>
      </ByokFlagGate>
    </LastWorkspaceResolver>
  );
}

// Object-type settings gate (FPS-2357), shared by the index, `/new`, and `/edit`
// routes. The Ember workspace-settings parent gated every child on
// `journeys/workspace_settings/view` before the objects route's own Builder-plan
// redirect ran, so this gate checks in the same order.
type WorkspaceObjectsSettingsGateProps = {
  children: ReactNode;
};

export function WorkspaceObjectsSettingsGate({ children }: WorkspaceObjectsSettingsGateProps) {
  const router = useRouter();

  return (
    <ProtectedRoute
      permission="journeys/workspace_settings/view"
      errorMessage="You do not have permission to view workspace settings. If you'd like access, let an Admin know."
      allowReadOnlyMasqAccess={true}
      redirectTo={(params) =>
        router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
          .href
      }
    >
      <WorkspaceObjectsSettingsBuilderGate>{children}</WorkspaceObjectsSettingsBuilderGate>
    </ProtectedRoute>
  );
}

type WorkspaceObjectsSettingsBuilderGateProps = {
  children: ReactNode;
};

function WorkspaceObjectsSettingsBuilderGate({ children }: WorkspaceObjectsSettingsBuilderGateProps) {
  const account = useStore(accountStore);

  // The available-plans page is Ember-owned while the billing flag is off, so
  // route through the safe-navigation machinery (replacing history, as Ember's
  // `replaceWith` did).
  if (isBuilderPlan(account)) {
    return (
      <SafeNavigate to="/settings/billing/available-plans/journeys" options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  return <>{children}</>;
}

export function WorkspaceObjectsSettingsIndexRoute() {
  return (
    <LastWorkspaceResolver>
      <WorkspaceObjectsSettingsGate>
        <LazyPage>
          <WorkspaceObjectsSettingsPage />
        </LazyPage>
      </WorkspaceObjectsSettingsGate>
    </LastWorkspaceResolver>
  );
}

// Object-type edit page (FPS-2941). Ember's edit route redirected users without
// `journeys/object_types/view` back to the objects index; the page renders
// read-only for viewers without `journeys/object_types/edit`. Shares the
// permission/Builder-plan gate with the index and `/new` routes.
export function WorkspaceObjectsSettingsEditRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <WorkspaceObjectsSettingsGate>
        <ProtectedRoute
          permission="journeys/object_types/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/objects',
              params: { workspaceId: params.workspaceId ?? 'last' },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceObjectsSettingsEditPage />
          </LazyPage>
        </ProtectedRoute>
      </WorkspaceObjectsSettingsGate>
    </LastWorkspaceResolver>
  );
}

// Object-type create/setup page (FPS-2942). Ember's `/new` route redirected users
// without `journeys/object_types/create` back to the objects index (with the
// "not allowed" snackbar, which is `ProtectedRoute`'s default message). A write
// route admits no read-only masqueraders, so no `allowReadOnlyMasqAccess`. Shares
// the permission/Builder-plan gate with the index and `/edit` routes.
export function WorkspaceObjectsSettingsNewRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <WorkspaceObjectsSettingsGate>
        <ProtectedRoute
          permission="journeys/object_types/create"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/objects',
              params: { workspaceId: params.workspaceId ?? 'last' },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceObjectsSettingsNewPage />
          </LazyPage>
        </ProtectedRoute>
      </WorkspaceObjectsSettingsGate>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceCustomSmsSettingsRoute() {
  return (
    <LazyPage>
      <WorkspaceCustomSmsSettingsPage />
    </LazyPage>
  );
}

export function WorkspaceCustomSmsCredentialDetailRoute() {
  return (
    <LazyPage>
      <WorkspaceCustomSmsCredentialDetailPage />
    </LazyPage>
  );
}

// Email settings permission gate (FPS-2349), shared by the tabbed shell and every tab below it.
// The permission check mirrors the workspace-settings parent every one of these routes sits under,
// which admits a read-access masquerade regardless of the workspace permission.
type WorkspaceEmailSettingsGateProps = {
  children: ReactNode;
};

function WorkspaceEmailSettingsGate({ children }: WorkspaceEmailSettingsGateProps) {
  const router = useRouter();

  return (
    <ProtectedRoute
      permission="journeys/workspace_settings/view"
      errorMessage="You do not have permission to view workspace settings. If you'd like access, let an Admin know."
      allowReadOnlyMasqAccess={true}
      redirectTo={(params) =>
        router.buildLocation({
          to: '/workspaces/$workspaceId/settings',
          params: { workspaceId: params.workspaceId ?? 'last' },
        }).href
      }
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * The tabbed Email settings shell. Holds the gate for the whole subtree — a tab route only ever
 * renders through this layout, so the permission check lives here once.
 */
export function WorkspaceEmailSettingsLayout() {
  return (
    <LastWorkspaceResolver>
      <WorkspaceEmailSettingsGate>
        <LazyPage>
          <WorkspaceEmailSettingsPage />
        </LazyPage>
      </WorkspaceEmailSettingsGate>
    </LastWorkspaceResolver>
  );
}

/** The Sending domains tab's URL, the landing tab the legacy email URLs send people to. */
function useSendingDomainsHref() {
  const router = useRouter();
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? ('last' as const);

  return router.buildLocation({
    to: '/workspaces/$workspaceId/settings/actions/email/sending_domains',
    params: { workspaceId },
  }).href;
}

// `/settings/actions/email` has no landing page of its own — it opens on Sending domains. The
// redirect replaces, so the bare email URL never lands in history and Back leaves the section.
export function WorkspaceEmailSettingsIndexRoute() {
  const sendingDomainsHref = useSendingDomainsHref();

  return <Navigate to={sendingDomainsHref} replace />;
}

// `/settings/actions/email/from_addresses` is a legacy URL kept working for old links; from
// addresses are managed on the Sending domains tab.
export function WorkspaceEmailFromAddressesRoute() {
  const sendingDomainsHref = useSendingDomainsHref();

  return <Navigate to={sendingDomainsHref} replace />;
}

// `/settings/actions/email/deliverability` is a legacy URL from when deliverability was its own tab.
export function WorkspaceEmailDeliverabilityRoute() {
  const sendingDomainsHref = useSendingDomainsHref();

  return <Navigate to={sendingDomainsHref} replace />;
}

export function WorkspaceEmailSendingDomainsIndexRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailSendingDomainsPage />
    </LazyPage>
  );
}

export function WorkspaceEmailSendingDomainDetailRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailSendingDomainDetailPage />
    </LazyPage>
  );
}

export function WorkspaceEmailDynamicAddressesRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailDynamicAddressesPage />
    </LazyPage>
  );
}

export function WorkspaceEmailSmtpRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailSmtpPage />
    </LazyPage>
  );
}

export function WorkspaceEmailDefaultsRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailDefaultsPage />
    </LazyPage>
  );
}

export function WorkspaceEmailSuppressionListRoute() {
  return (
    <LazyPage>
      <WorkspaceEmailSuppressionListPage />
    </LazyPage>
  );
}

export function HarSanitizerRoute() {
  return (
    <LazyPage>
      <HarSanitizerPage />
    </LazyPage>
  );
}

export function NoAccessibleWorkspacesRoute() {
  return (
    <LazyPage>
      <NoAccessibleWorkspacesPage />
    </LazyPage>
  );
}

// Guest-invitation accept page (standalone, chrome-less, reached from an email link).
export function AcceptGuestInvitationRoute() {
  return (
    <LazyPage>
      <AcceptGuestInvitationPage />
    </LazyPage>
  );
}

export function PostCancelRoute() {
  return (
    <LazyPage>
      <PostCancelPage />
    </LazyPage>
  );
}

// The account-closed notice. It renders outside the authenticated layout, because it must show no
// navigation chrome, so the page reads the account and gates itself. This app owns the URL always:
// there is no flag and no fallback.
export function AccountCanceledRoute() {
  return (
    <LazyPage>
      <AccountCanceledPage />
    </LazyPage>
  );
}

// Both pages are served at two URL segments — `/analysis/...` and, once the Analysis →
// Reports rename flag is on, `/reports/...`. `AnalysisRouteView` renders whichever is
// canonical for the account and redirects the other, so each route passes its own segment.
type AnalysisRouteProps = { segment: AnalysisSegment; workspaceId: WorkspaceIdRouteParamInput };

export function DeliverabilityRoute({ segment, workspaceId }: AnalysisRouteProps) {
  return (
    <AnalysisRouteView segment={segment} page="deliverability" workspaceId={workspaceId}>
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <LastWorkspaceResolver>
          <LazyPage>
            <DeliverabilityPage />
          </LazyPage>
        </LastWorkspaceResolver>
      </PlanGuard>
    </AnalysisRouteView>
  );
}

export function EmailClientsRoute({ segment, workspaceId }: AnalysisRouteProps) {
  return (
    <AnalysisRouteView segment={segment} page="email-clients" workspaceId={workspaceId}>
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <LastWorkspaceResolver>
          <LazyPage>
            <EmailClientsPage />
          </LazyPage>
        </LastWorkspaceResolver>
      </PlanGuard>
    </AnalysisRouteView>
  );
}

// The Ember `layout` (detail) route nests under the `layouts` route, whose
// `beforeModel` redirected builder-plan accounts away — mirrored with
// PlanGuard. It had no permission guard of its own (permissions only change
// what the page renders).
export function LayoutDetailRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <LayoutDetailPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

// Like the detail route above, the Ember `layout.edit` and `layouts.new`
// routes nest under the builder-plan-gated `layouts` route and had no
// permission guard of their own.
export function LayoutEditRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <LayoutEditPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

export function NewLayoutRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <NewLayoutPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

// Mirrors Ember's `layouts.starters` route guards: the parent `layouts` route
// redirected builder-plan accounts away, and the starters route itself
// required `journeys/layouts/create` (falling back to the layouts list).
export function LayoutsStartersRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      {/* PlanGuard sits outside ProtectedRoute to match Ember's guard order:
          the parent `layouts` route redirected builder-plan accounts (to the
          available-plans page) before the starters route's permission check
          ever ran. */}
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <ProtectedRoute
          permission="journeys/layouts/create"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/journeys/layouts',
              params: { workspaceId: params.workspaceId ?? 'last' },
            }).href
          }
        >
          <LazyPage>
            <LayoutStartersPage />
          </LazyPage>
        </ProtectedRoute>
      </PlanGuard>
    </LastWorkspaceResolver>
  );
}

// Builder-plan accounts are redirected away, matching the Ember route's
// `beforeModel` redirect to the available-plans page.
export function MessageLibraryRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <MessageLibraryPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

type ChildRouteProps = {
  children: ReactNode;
};

type GeofencesRouteProps = ChildRouteProps & {
  permission: Permission;
  redirectTo: (params: Record<string, string | undefined>) => string;
  /**
   * Require a real geoset in the URL before rendering. Set on the geoset-scoped
   * geofence pages (`/geosets/:geosetId/geofences/…`); left off for the geosets
   * index, which has no geoset id. See {@link GeosetRequiredGuard}.
   */
  requireGeoset?: boolean;
};

export function AiRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function AiSkillsRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function GeofencesRoute({ children, permission, redirectTo, requireGeoset = false }: GeofencesRouteProps) {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <PlanFeatureGuard
        feature={PlanFeature.Geofences}
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <ProtectedRoute permission={permission} redirectTo={redirectTo}>
          <LazyPage>{requireGeoset ? <GeosetRequiredGuard>{children}</GeosetRequiredGuard> : children}</LazyPage>
        </ProtectedRoute>
      </PlanFeatureGuard>
    </LastWorkspaceResolver>
  );
}

// Design Studio dashboard (CON-7495), fully rolled out and natively routed
// here. `route-ownership.ts` claims this URL unconditionally, so in-bundle
// navigations to it go through a full TanStack load and the native page
// renders inside the authenticated layout.
export function DesignStudioDashboardRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <DesignStudioDashboardPage />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

// Design Studio global styles (CON-7588), fully rolled out and natively routed
// here, like the dashboard route above. `route-ownership.ts` claims this URL
// unconditionally, so in-bundle navigations to it go through a full TanStack
// load and the native page renders inside the authenticated layout.
export function DesignStudioGlobalStylesRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <DesignStudioGlobalStylesPage />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function AssetsRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AssetsPage />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function SnippetsLayoutRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <SnippetsLayout />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

type ExperimentsWorkspaceGateProps = { children: ReactNode };

// Until the workspace list lands there is no current workspace, so `journeys/campaigns/view` is
// denied and `ProtectedRoute` redirects home. Hold render until the list resolves; an id the list
// does not carry then falls back to the default workspace, as it does on every other route.
function ExperimentsWorkspaceGate({ children }: ExperimentsWorkspaceGateProps) {
  const workspaces = useListWorkspaces();

  if (workspaces.isPending) {
    return <PageLoader />;
  }

  return <>{children}</>;
}

// Builder plans have no experiments access at all. Services 403s every experiment read with the
// flag off, so the flag gate belongs here: nothing below it can render without the API.
export function ExperimentsLayoutRoute() {
  const router = useRouter();
  const isBuilderPlan = useStore(billing.isBuilderPlan);

  if (isBuilderPlan) {
    return <NotFoundPage />;
  }

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.ExperimentAction} fallback={ExperimentsUnavailable}>
      <LastWorkspaceResolver>
        <ExperimentsWorkspaceGate>
          <ProtectedRoute
            permission="journeys/campaigns/view"
            redirectTo={(params) =>
              router.buildLocation({
                to: '/workspaces/$workspaceId/home',
                params: { workspaceId: params.workspaceId },
              }).href
            }
          >
            <LazyPage>
              <ExperimentsLayout />
            </LazyPage>
          </ProtectedRoute>
        </ExperimentsWorkspaceGate>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

// Essentials onboarding (SELF-138): the top-level, chrome-less `/onboarding/*` layout.
// Net-new Hydra routes (no Ember equivalent), so route ownership is ungated and this
// guard's NotFound fallback is the flag-off behavior. Like `PromoCheckoutRoute` above,
// the route lives outside `_authenticated`, so nothing holds render while auth is in
// flight and `FeatureFlagGuard` would read the flag's default (false) from the empty
// accountStore — a flag-on cold load would flash NotFound. Hold behind a loader until
// auth settles; never read flags in `beforeLoad`.
export function OnboardingLayout() {
  const { data, error, isError, isLoading: isAuthLoading, refetch } = useAuthQuery();

  if (isAuthLoading) {
    return <BrandLoader />;
  }

  // The server refused the session (logged out / expired). This tree is
  // chrome-less and outside `_authenticated`, so none of that layout's login
  // recovery runs — and with `essentials_onboarding` unreadable the flag guard
  // below would render NotFound, dead-ending e.g. a bookmarked
  // /onboarding/verification visit after the session lapsed. Send them to
  // login with the attempted URL captured (the authenticated layout's
  // pattern); NotFound stays the answer only for authenticated accounts
  // without the flag.
  if (isSessionRejection(error)) {
    return <Navigate to="/login" saveCurrentUrl={true} />;
  }

  // Both stores must be populated before mounting children — the onboarding
  // pages and gate call `useAuth()`, which assumes account AND user (same
  // requirement the authenticated shell enforces before rendering).
  if (data?.account == null || data.user == null) {
    // Transient failure (5xx / network) with nothing to render from: the
    // session may be perfectly valid, so show the retry surface rather than
    // misclassifying it as "logged out" and bouncing to /login (the 5xx
    // flavor of FPS-2996).
    if (isError) {
      return (
        <AppErrorFallback
          error={error}
          resetErrorBoundary={() => {
            void refetch();
          }}
        />
      );
    }
    return <Navigate to="/login" saveCurrentUrl={true} />;
  }

  // A promo account that has not added a card yet belongs on checkout, not in
  // the wizard: the flow is signup → checkout → wizard. This tree sits outside
  // `_authenticated`, so the app-entry gate that enforces the card everywhere
  // else never runs here. Masquerading staff are exempt, as they are there.
  if (data.account.is_masq !== true && requiresCardCheckout(data.account)) {
    return <Navigate to={PROMO_CHECKOUT_PATH} replace={true} />;
  }

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.EssentialsOnboarding} fallback={NotFoundPage}>
      <Outlet />
    </FeatureFlagGuard>
  );
}

// Bare `/onboarding` never renders content — it forwards to the right page for the
// account's state (the same pure rules the pages themselves apply).
export function OnboardingIndexRedirect() {
  const { redirect, isLoading } = useOnboardingRedirect('index');

  // Hold while the gate's signals load (the redirect is deliberately
  // withheld). Once they settle, 'index' always resolves to a redirect — the
  // null fallback guards the type, not a real state.
  if (isLoading || redirect == null) {
    return <PageLoader />;
  }
  return <SafeNavigate to={redirect} options={{ shouldReplaceHistoryEntry: true }} />;
}

export function OnboardingVerificationRoute() {
  return (
    <LazyPage>
      <OnboardingVerificationPage />
    </LazyPage>
  );
}

// The static get-started question is gone — the wizard owns onboarding. The
// URL stays alive for stale links and forwards into it.
export function OnboardingGetStartedRoute() {
  return <SafeNavigate to={ONBOARDING_PATHS.wizard} options={{ shouldReplaceHistoryEntry: true }} />;
}

export function OnboardingWizardRoute() {
  return (
    <Suspense fallback={null}>
      <OnboardingWizardPage />
    </Suspense>
  );
}

// The migrated automation workflow builder URL (FPS-2338), dark behind
// `workflow-builder-migration`.
// The flag branch is the outermost wrapper: flag off means Hydra does nothing at this URL, so
// no workspace resolver may run above it — a sentinel resolver renders a
// safe-navigate that parks until the embedded app reports itself loaded, which can never
// happen when the resolver renders instead of that app.
type WorkflowBuilderRouteProps = { campaignId: string };

export function WorkflowBuilderRoute({ campaignId }: WorkflowBuilderRouteProps) {
  return (
    <WorkflowBuilderRouteView campaignId={campaignId}>
      {/* Builder-plan accounts have no campaigns and are sent to available plans, matching what
          the embedded app does on the route this takes over. Inside the flag branch, so a
          flag-off account still reaches the embedded app and its own redirect. */}
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <LastWorkspaceResolver>
          <LazyPage>
            <WorkflowBuilderPage campaignId={campaignId} />
          </LazyPage>
        </LastWorkspaceResolver>
      </PlanGuard>
    </WorkflowBuilderRouteView>
  );
}

// The overview Workflow tab's `journeys` URL, under both taxonomy spellings, on the same flag.
// The embedded app serves this exact address when the flag is off, so the branch renders it in
// place rather than handing off. `appBundle` is needed here (and not on the canonical URL)
// because this shape keeps the `journeys` segment the sentinel resolver rebuilds targets from.
type OverviewWorkflowRouteProps = { campaignId: string };

export function OverviewWorkflowRoute({ campaignId }: OverviewWorkflowRouteProps) {
  return (
    <OverviewWorkflowRouteView>
      {/* Same Builder-plan redirect as the canonical URL above, for the same reason. */}
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <LastWorkspaceResolver appBundle={AppBundle.Journeys}>
          <LazyPage>
            <WorkflowBuilderPage campaignId={campaignId} />
          </LazyPage>
        </LastWorkspaceResolver>
      </PlanGuard>
    </OverviewWorkflowRouteView>
  );
}
